import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getUsersDetails,
  getUserBuildingsDetails,
  getUserBuildingDetails,
  createUser,
  updateUser,
  deleteUser,
  updatePermissions,
  updateUserPassword,
  deleteUserPermissions,
} from '../../services/usersService';

// Fetch all users with detailed information
export const fetchUsersDetails = createAsyncThunk('users/fetchUsersDetails', async (params, { rejectWithValue }) => {
  try {
    const res = await getUsersDetails();
    return res;
  } catch (error) {
    console.error('Error fetching users details:', error);
    return rejectWithValue(error.message);
  }
});

// Get users from state, fetch if empty
export const getUsers = createAsyncThunk('users/getUsers', async (params, { getState, dispatch }) => {
  const state = getState();
  const existingUsers = state.users.users;

  // If we already have users data, return it
  if (existingUsers && existingUsers.length > 0) {
    return existingUsers;
  }

  // If no data exists, fetch it
  await dispatch(fetchUsersDetails(params));

  // Return the newly fetched data
  const updatedState = getState();
  return updatedState.users.users;
});

// manage-buildings - building service
export const fetchUserBuildingsDetails = createAsyncThunk(
  'buildings/fetchUserBuildingsDetails',
  async (params, { rejectWithValue }) => {
    try {
      const customerId = params?.customerId || '';
      const res = await getUserBuildingsDetails(customerId);
      return res; // Let the extraReducer handle setting the state
    } catch (error) {
      console.error('Error fetching building details:', error);
      return rejectWithValue(error.message);
    }
  },
);

// manage-buildings - building service
export const fetchUserBuildingDetails = createAsyncThunk(
  'building/fetchUserBuildingDetails',
  async (params, { rejectWithValue }) => {
    try {
      const res = await getUserBuildingDetails(params.buildingId);
      return res; // Let the extraReducer handle setting the state
    } catch (error) {
      console.error('Error fetching building details:', error);
      return rejectWithValue({ message: error.message, statusCode: error.statusCode });
    }
  },
);

// Get buildings from state, fetch if empty
export const getBuildings = createAsyncThunk('users/getBuildings', async (params, { getState, dispatch }) => {
  const state = getState();
  const existingBuildings = state.users.buildings;

  // If we already have buildings data, return it
  if (existingBuildings && existingBuildings.length > 0) {
    return existingBuildings;
  }

  // If no data exists, fetch it
  await dispatch(fetchUserBuildingsDetails(params));

  // Return the newly fetched data
  const updatedState = getState();
  return updatedState.users.buildings;
});

// manage-users - user service
export const createUserData = createAsyncThunk('users/createUser', async (userData, { dispatch }) => {
  const res = await createUser(userData);
  // Refresh users list after successful creation
  if (res.success) {
    dispatch(fetchUsersDetails());
  }
  return res;
});

// manage-users - user service
export const updateUserData = createAsyncThunk('users/updateUser', async (userData, { dispatch }) => {
  const res = await updateUser(userData);
  // Refresh users list after successful update
  if (res.success) {
    dispatch(fetchUsersDetails());
  }
  return res;
});

// manage-users - user service
export const changeUserPassword = createAsyncThunk('users/updateUserPassword', async (newPassword, { dispatch }) => {
  const res = await updateUserPassword(newPassword);
  return res;
});

// manage-users - user service
export const deleteUserData = createAsyncThunk('users/deleteUser', async (userId, { dispatch }) => {
  const res = await deleteUser(userId);
  // Refresh users list after successful deletion
  if (res.success) {
    dispatch(fetchUsersDetails());
  }
  return { userId, ...res };
});

export const removeUserPermissions = createAsyncThunk('users/deleteUserPermissions', async (userId, { dispatch }) => {
  const res = await deleteUserPermissions(userId);
  // Refresh users list after successful deletion
  if (res.success) {
    dispatch(fetchUsersDetails());
  }
  return { userId, ...res };
});

export const updateUserPermissions = createAsyncThunk(
  'users/updateUserPermissions',
  async (permissionData, { dispatch }) => {
    const res = await updatePermissions(permissionData);
    if (res.success) {
      dispatch(fetchUsersDetails());
    }
    return res;
  },
);

//LEGACY - in many places in app

const usersInitialState = {
  users: [],
  buildings: [],
  filteredUsers: [],
  loading: false,
  //not in use ? - LEGACY
  usersLov: [],
};

export const usersSlice = createSlice({
  name: 'users',
  initialState: usersInitialState,
  reducers: {
    clear: state => {
      state.usersInitialState = usersInitialState;
      // return usersInitialState;
    },
    setFilteredUsers: (state, action) => {
      state.filteredUsers = action.payload;
    },
  },
  extraReducers: {
    // Fetch users details
    [fetchUsersDetails.pending]: state => {
      state.loading = true;
    },
    [fetchUsersDetails.fulfilled]: (state, action) => {
      state.loading = false;
      state.users = action.payload;
      state.filteredUsers = action.payload; // Initially show all users
    },
    [fetchUsersDetails.rejected]: (state, action) => {
      state.loading = false;
      state.users = [];
      state.filteredUsers = [];
      console.error('Failed to fetch users details:', action.payload);
    },
    [fetchUserBuildingsDetails.pending]: state => {
      state.loading = true;
    },
    [fetchUserBuildingsDetails.fulfilled]: (state, action) => {
      state.buildings = action.payload;
      state.loading = false;
    },
    [fetchUserBuildingsDetails.rejected]: (state, action) => {
      state.buildings = [];
      state.loading = false;
      console.error('Failed to fetch building details:', action.payload);
    },

  },
});
